/**
 * AppSeparatorNode - Custom DecoratorNode for horizontal rules
 *
 * Replaces the generic HorizontalRuleNode with a node that:
 * - Renders the app's Bézier SVG Separator atom in normal mode
 * - Shows "---" raw markdown text when the cursor is on an adjacent block
 *
 * Adjacent detection: if the focused block is the sibling immediately before
 * or after this node, we show source mode.
 *
 * Architecture:
 * - DecoratorNode subclass (contentEditable=false)
 * - Adjacency detected inside the React component via registerUpdateListener
 * - CSS handles the source-mode text style
 *
 * Markdown:
 * - Import: HR_TRANSFORMER in WorkingDocEditor creates this node when `---`/`***`/`___` is typed
 * - Export: CODE_BLOCK_DECORATOR_TRANSFORMER pattern — returns '---'
 */

import { useEffect, useState, type JSX } from 'react';
import type { LexicalEditor, NodeKey, SerializedLexicalNode } from 'lexical';
import {
  DecoratorNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Separator } from '@/shared/ui/separator';
import type { ElementTransformer } from '@lexical/markdown';

// ─── React component ──────────────────────────────────────────────────────────

interface AppSeparatorComponentProps {
  nodeKey: NodeKey;
}

const AppSeparatorComponent = ({ nodeKey }: AppSeparatorComponentProps) => {
  const [editor] = useLexicalComposerContext();
  const [isAdjacent, setIsAdjacent] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setIsAdjacent(false);
          return;
        }

        const anchor = selection.anchor.getNode();
        let focusedKey: string | null = null;
        try {
          // For adjacency detection we need the top-level block key (ListNode or paragraph etc.)
          focusedKey = anchor.getTopLevelElementOrThrow().getKey();
        } catch {
          focusedKey = null;
        }

        if (!focusedKey) {
          setIsAdjacent(false);
          return;
        }

        const node = $getNodeByKey(nodeKey);
        if (!node) {
          setIsAdjacent(false);
          return;
        }

        const prevSibling = node.getPreviousSibling();
        const nextSibling = node.getNextSibling();

        const adjacent =
          prevSibling?.getKey() === focusedKey || nextSibling?.getKey() === focusedKey;

        setIsAdjacent(adjacent);
      });
    });
  }, [editor, nodeKey]);

  if (isAdjacent) {
    return (
      <div className={SOURCE_MODE} contentEditable={false}>
        ---
      </div>
    );
  }

  return (
    <div contentEditable={false}>
      <Separator />
    </div>
  );
};

// ─── DecoratorNode class ──────────────────────────────────────────────────────

/* Mode source : affiche le `---` brut quand le curseur est adjacent. */
const SOURCE_MODE =
  'text-fg-secondary pointer-events-none py-1 font-mono text-sm leading-normal opacity-55 select-none';

export class AppSeparatorNode extends DecoratorNode<JSX.Element> {
  static override getType(): string {
    return 'app-separator';
  }

  static override clone(node: AppSeparatorNode): AppSeparatorNode {
    return new AppSeparatorNode(node.__key);
  }

  static override importJSON(_json: SerializedLexicalNode): AppSeparatorNode {
    return new AppSeparatorNode();
  }

  override exportJSON(): SerializedLexicalNode {
    return {
      type: 'app-separator',
      version: 1,
    };
  }

  override createDOM(): HTMLElement {
    return document.createElement('div');
  }

  override updateDOM(): boolean {
    return false;
  }

  override isInline(): boolean {
    return false;
  }

  override decorate(_editor: LexicalEditor): JSX.Element {
    return <AppSeparatorComponent nodeKey={this.__key} />;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function $isAppSeparatorNode(node: unknown): node is AppSeparatorNode {
  return node instanceof AppSeparatorNode;
}

export function $createAppSeparatorNode(): AppSeparatorNode {
  return new AppSeparatorNode();
}

// ─── Markdown transformer ─────────────────────────────────────────────────────

export const APP_SEPARATOR_TRANSFORMER: ElementTransformer = {
  dependencies: [AppSeparatorNode],
  export: (node) => ($isAppSeparatorNode(node) ? '---' : null),
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode) => {
    const separator = $createAppSeparatorNode();
    parentNode.replace(separator);
    // DecoratorNode is contentEditable=false — cursor can't land inside it.
    // Insert a paragraph after and move selection there so Lexical doesn't
    // revert the update due to a stranded selection.
    const paragraph = $createParagraphNode();
    separator.insertAfter(paragraph);
    paragraph.select();
  },
  type: 'element',
};
